import { Status } from '../../planBT/types/planBT.types';

export type PoleType = "WOOD" | "CONCRETE" | "STEEL";

// Enum pour tracker les actions sur les éléments et connexions
export enum ElementAction {
  TO_CREATE = 'TO_CREATE',
  TO_UPDATE = 'TO_UPDATE', 
  TO_DELETE = 'TO_DELETE',
  UN_CHANGED = 'UN_CHANGED'
}

export interface Project {
  id: string;
  name: string;
  clientName: string;
  status: 'in-progress' | 'completed' | 'pending';
  createdAt: string;
  lastModified: string;
  description: string;
  totalPower: number;
  elementsCount: number;
  progress: number;
  mapBackground?: string;
  elements: GridElement[];
  connections: Connection[];
}

export interface GridElement {
  id: string;
  type: 'pole' | 'power-plant' | 'meter' | 'transformer';
  x: number;
  y: number;
  name: string;
  number?: string; // Numéro de l'élément (associé au mtr_number pour les meters)
  properties: ElementProperties;
  status: Status;
  lights?: Light[];
  nodeId?: string;
  hostId?: string;
  // Coordonnées GPS originales
  gpsCoordinates?: {
    latitude: number;
    longitude: number;
  };
  // Action pour tracker les modifications
  action?: ElementAction;
  // ID du poteau connecté (pour les compteurs)
  connectedPoleId?: string;
  // Flags de synchronisation 8000
  isJouly?: boolean;   // true si le compteur est de type Jouly (badge violet)
  // ID de l'association boîte dans le système 8000 (pour la mise à jour GPS)
  boxAssoId?: string;
}

export interface Light {
  id: string;
  type: 'street-light' | 'flood-light' | 'decorative';
  power: number; // Watts
  status: 'on' | 'off' | 'maintenance';
  name?: string;
  number?: string; // Numéro du lampadaire
  zhagaDevui?: string;
  // Action pour tracker les modifications
  action?: ElementAction;
  // Référence au pôle parent
  poleId?: string;
}

export interface ElementProperties {
  power?: number; // kW
  voltage?: number; // V
  current?: number; // A
  consumption?: number; // kWh
  capacity?: number; // kW
  efficiency?: number; // %
  mtr_number?: string; // Numéro du compteur
  pole_type?: PoleType; // Type de poteau
  mergedFrom?: number; // Nombre d'éléments fusionnés
  [key: string]: any; // Permettre des propriétés supplémentaires
}

export interface Connection {
  id: string;
  name?: string; // Nom de la connexion (ex: "3x150+70", "2x16", etc.)
  fromId: string;
  toId: string;
  type: 'power-line' | 'data-line';
  voltage: number;
  resistance: number;
  length: number;
  status: Status;
  // Coordonnées GPS optionnelles pour les connexions importées
  fromCoordinates?: { latitude: number; longitude: number };
  toCoordinates?: { latitude: number; longitude: number };
  // Action pour tracker les modifications
  action?: ElementAction;
}

// GeoJSON related types for the local GeoJSON functionality
export interface GeoJSONFeature {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: {
    type: 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon';
    coordinates: number[] | number[][] | number[][][];
  };
  id?: string | number;
}

export interface GeoJSONData {
  type: 'FeatureCollection' | 'Feature';
  features?: GeoJSONFeature[];
  properties?: Record<string, unknown>;
  geometry?: GeoJSONFeature['geometry'];
}

export interface GeoJSONLayer {
  id: string;
  name: string;
  data: GeoJSONData;
  visible: boolean;
  color: string;
  opacity: number;
}

// Type pour le résumé des changements dans le GridEditor
export interface ChangesSummaryPlanBT {
  elements: {
    create: GridElement[];
    update: GridElement[];
    delete: GridElement[];
  };
  connections: {
    create: Connection[];
    update: Connection[];
    delete: Connection[];
  };
  streetLights: {
    create: Light[];
    update: Light[];
    delete: Light[];
  };
}

// Types d'erreurs pour le GridEditor
export enum GridEditorErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  PERMISSION_ERROR = 'PERMISSION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  DUPLICATE_ERROR = 'DUPLICATE_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface GridEditorError {
  type: GridEditorErrorType;
  message: string;
  details?: any;
  elementId?: string;
  operation?: 'create' | 'update' | 'delete';
  retryable?: boolean;
}

export interface SaveChangesResult {
  success: boolean;
  errors?: GridEditorError[];
  partialSuccess?: boolean;
  message?: string;
  nodeIdMap?: Record<string, string>; // frontendId → couchbaseId pour les nœuds nouvellement créés
}
